/**
 * Chat E2EE primitives (ADR-011 phase 2).
 *
 * Pure logic, no DOM, no storage, no network — the same shape as
 * identity.mjs and tunnelKdf.mjs, so it runs under `node --test` and can be
 * checked against the same vectors by the Swift and Kotlin ports.
 *
 * Nothing here decides policy. It derives keys, wraps them, and seals and
 * opens envelopes. Who may talk to whom, when an epoch rotates and what is
 * stored belong to the layers above.
 *
 * Byte forms are part of the specification, not implementation detail: three
 * platforms that each pick their own base64 variant or their own integer
 * ordering will produce keys that do not meet, and the symptom is a Poly1305
 * failure for some pairs of users and not others.
 */
import nacl from 'tweetnacl';

// Domain tags. A hash or signature made under one must never validate under
// another — including the tunnel's own tags, which are bound to a room number
// and are meaningless for chat.
export const TAG_DEVICE_ID = 'VoxioxDeviceIdV1';
export const TAG_BUNDLE    = 'VoxioxDeviceBundleV1';
export const TAG_MSG_KEY   = 'VoxioxChatMsgV1';
export const TAG_KEY_ID    = 'VoxioxChatKeyIdV1';
export const TAG_PREV      = 'VoxioxPrevV1';

export const ENVELOPE_VERSION = 1;
export const FRAME_MAGIC = 0x01;
export const PAD_BLOCK = 256;
export const HEADER_BYTES = 1 + 8 + 4 + 8 + 16 + 24;   // 61, see ADR-011 §3.2

const enc = new TextEncoder();
const dec = new TextDecoder();

// ─── Bytes ──────────────────────────────────────────────────────────────────

export function concat(...parts) {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) { out.set(p, off); off += p.length; }
  return out;
}

export function u32be(n) {
  const b = new Uint8Array(4);
  b[0] = (n >>> 24) & 0xff; b[1] = (n >>> 16) & 0xff;
  b[2] = (n >>> 8) & 0xff;  b[3] = n & 0xff;
  return b;
}

export function u64be(n) {
  const b = new Uint8Array(8);
  let v = BigInt(n);
  for (let i = 7; i >= 0; i--) { b[i] = Number(v & 0xffn); v >>= 8n; }
  return b;
}

export function readU32be(b, off = 0) {
  return ((b[off] << 24) | (b[off + 1] << 16) | (b[off + 2] << 8) | b[off + 3]) >>> 0;
}

/** base64url without padding — the only form allowed on the wire and in SQL. */
export function b64u(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function unb64u(str) {
  const s = String(str).replace(/-/g, '+').replace(/_/g, '/');
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const bin = atob(s + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function equalBytes(a, b) {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
  return diff === 0;
}

// ─── Conversation id ────────────────────────────────────────────────────────

/**
 * The canonical 8 bytes naming a conversation: the two user ids, smaller
 * first, big-endian.
 *
 * Compared as NUMBERS. Ids travel as strings in places, and sorting them as
 * strings puts "10" before "9" — so the pair (9, 10) would derive two
 * different keys on the two sides and fail Poly1305, while most pairs worked.
 * A bug that hits some users and not others is the worst kind to chase.
 */
export function conversationId(uidA, uidB) {
  const a = Number(uidA), b = Number(uidB);
  if (!Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < 0) {
    throw new Error('conversationId: user ids must be non-negative integers');
  }
  if (a === b) throw new Error('conversationId: a conversation needs two different users');
  return concat(u32be(Math.min(a, b)), u32be(Math.max(a, b)));
}

// ─── Device identity ────────────────────────────────────────────────────────

/**
 * 16 raw bytes naming a device, derived from BOTH of its public keys.
 *
 * The DH key has to be inside: the fingerprint people read to each other must
 * cover the key their conversation key gets encrypted to. Cover only the
 * signing key and a server can swap the DH key, leave the fingerprint
 * unchanged, and be handed every conversation key from then on.
 */
export function deviceId(sigPub, dhPub) {
  if (sigPub?.length !== 32 || dhPub?.length !== 32) {
    throw new Error('deviceId: both public keys must be 32 bytes');
  }
  return nacl.hash(concat(enc.encode(TAG_DEVICE_ID), sigPub, dhPub)).slice(0, 16);
}

export function deviceIdB64u(sigPub, dhPub) { return b64u(deviceId(sigPub, dhPub)); }

/** Bytes a device signs to prove it holds both secret keys. */
export function bundlePayload(userId, sigPub, dhPub, createdAtMs) {
  return concat(
    enc.encode(TAG_BUNDLE), u32be(Number(userId)),
    deviceId(sigPub, dhPub), sigPub, dhPub, u64be(createdAtMs),
  );
}

export function signBundle(sigSecret, userId, sigPub, dhPub, createdAtMs) {
  return nacl.sign.detached(bundlePayload(userId, sigPub, dhPub, createdAtMs), sigSecret);
}

export function verifyBundle(sig, userId, sigPub, dhPub, createdAtMs) {
  try {
    return nacl.sign.detached.verify(bundlePayload(userId, sigPub, dhPub, createdAtMs), sig, sigPub);
  } catch (e) { return false; }
}

// ─── Conversation key ───────────────────────────────────────────────────────

export function generateConversationKey() { return nacl.randomBytes(32); }

/** Which CK a frame was sealed with, without naming the key itself. */
export function keyId(ck) {
  if (ck?.length !== 32) throw new Error('keyId: CK must be 32 bytes');
  return nacl.hash(concat(enc.encode(TAG_KEY_ID), ck)).slice(0, 8);
}

/**
 * The per-message key.
 *
 * The sender's device id goes in because secretbox has no place for
 * associated data, and this buys it for nothing: a server that rewrites who
 * sent a row cannot re-derive the key, and a changed epoch or conversation in
 * the cleartext header simply fails to open.
 */
export function deriveMessageKey(ck, convId, epoch, senderDeviceId) {
  if (ck?.length !== 32) throw new Error('deriveMessageKey: CK must be 32 bytes');
  if (convId?.length !== 8) throw new Error('deriveMessageKey: convId must be 8 bytes');
  if (senderDeviceId?.length !== 16) throw new Error('deriveMessageKey: device id must be 16 bytes');
  return nacl.hash(concat(
    enc.encode(TAG_MSG_KEY), ck, convId, u32be(epoch), senderDeviceId,
  )).slice(0, 32);
}

// ─── Key wrapping ───────────────────────────────────────────────────────────

/**
 * Encrypt CK to one device. The epoch and conversation travel inside, so a
 * wrap cannot be replayed into another conversation — and unwrap checks them,
 * which is the step everybody forgets.
 */
export function wrapKey(ck, epoch, convId, theirDhPub, myDhSecret) {
  if (ck?.length !== 32) throw new Error('wrapKey: CK must be 32 bytes');
  const nonce = nacl.randomBytes(24);
  const boxed = nacl.box(concat(ck, u32be(epoch), convId), nonce, theirDhPub, myDhSecret);
  if (!boxed) throw new Error('wrapKey: box failed');
  return { nonce, boxed };
}

export function unwrapKey(wrap, expectedEpoch, expectedConvId, theirDhPub, myDhSecret) {
  const opened = nacl.box.open(wrap.boxed, wrap.nonce, theirDhPub, myDhSecret);
  if (!opened || opened.length !== 44) return null;
  const ck = opened.slice(0, 32);
  const epoch = readU32be(opened, 32);
  const convId = opened.slice(36, 44);
  // Refusing here is the whole point of putting them in.
  if (epoch !== expectedEpoch) return null;
  if (!equalBytes(convId, expectedConvId)) return null;
  return ck;
}

// ─── Padding ────────────────────────────────────────────────────────────────

/**
 * uint16be(length) || plaintext || zeros, to a multiple of 256.
 *
 * It makes "ок" and "хорошо" the same size on the wire. It does not hide a
 * paragraph from a word, and is not meant to.
 */
export function pad(plaintext) {
  if (plaintext.length > 0xffff) throw new Error('pad: message too long');
  const total = Math.ceil((plaintext.length + 2) / PAD_BLOCK) * PAD_BLOCK;
  const out = new Uint8Array(total);
  out[0] = (plaintext.length >>> 8) & 0xff;
  out[1] = plaintext.length & 0xff;
  out.set(plaintext, 2);
  return out;
}

export function unpad(padded) {
  if (!padded || padded.length < 2) return null;
  const len = (padded[0] << 8) | padded[1];
  if (len + 2 > padded.length) return null;
  return padded.slice(2, 2 + len);
}

// ─── Envelope ───────────────────────────────────────────────────────────────

/**
 * Seal one message.
 *
 * The nonce is 24 random bytes, not a counter. A counter over a long-lived key
 * is the most likely catastrophic bug in a project with three ports — two
 * browser tabs on one store, a restored snapshot, a "let's reset the stuck
 * chat" — and a repeated (key, nonce) pair costs both the keystream and
 * authenticity. XSalsa20 with a 192-bit random nonce has no collision problem
 * at any real volume.
 */
export function sealEnvelope(envelope, { ck, convId, epoch, senderDeviceId }) {
  const key = deriveMessageKey(ck, convId, epoch, senderDeviceId);
  const nonce = nacl.randomBytes(24);
  const body = nacl.secretbox(pad(enc.encode(JSON.stringify(envelope))), nonce, key);
  return concat(
    new Uint8Array([FRAME_MAGIC]), convId, u32be(epoch), keyId(ck),
    senderDeviceId, nonce, body,
  );
}

/** Everything readable without a key — what a relay can see, and no more. */
export function parseFrameHeader(frame) {
  if (!frame || frame.length < HEADER_BYTES + 16 || frame[0] !== FRAME_MAGIC) return null;
  return {
    convId: frame.slice(1, 9),
    epoch: readU32be(frame, 9),
    keyId: frame.slice(13, 21),
    senderDeviceId: frame.slice(21, 37),
    nonce: frame.slice(37, 61),
    body: frame.slice(61),
  };
}

/**
 * Open a frame, or return null. Null covers every failure — wrong key, wrong
 * conversation, a tampered header — on purpose: a caller that cannot tell them
 * apart cannot leak which one it was.
 */
export function openEnvelope(frame, { ck, convId }) {
  const h = parseFrameHeader(frame);
  if (!h) return null;
  if (convId && !equalBytes(h.convId, convId)) return null;
  if (!equalBytes(keyId(ck), h.keyId)) return null;
  const key = deriveMessageKey(ck, h.convId, h.epoch, h.senderDeviceId);
  const padded = nacl.secretbox.open(h.body, h.nonce, key);
  if (!padded) return null;
  const plain = unpad(padded);
  if (!plain) return null;
  try {
    return { envelope: JSON.parse(dec.decode(plain)), header: h };
  } catch (e) { return null; }
}

/** Chain link over one's own previous frame — cheap ordering evidence. */
export function prevHash(previousFrame) {
  if (!previousFrame) return null;
  return nacl.hash(concat(enc.encode(TAG_PREV), previousFrame)).slice(0, 8);
}
